/**
 * TargetsPage.js
 * ===============
 * Page de gestion des objectifs CA (agence + par agent) — admin only.
 * Débloque les KPIs monthly_target_achievement, agent_ca_vs_target, pipeline_coverage.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Target, Users, Euro, Calendar, Save, TrendingUp } from 'lucide-react';

function formatEUR(n) {
    if (n === null || n === undefined || n === '') return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function TargetsPage() {
    const { user } = useAuth();
    const [month, setMonth] = useState(currentMonth());
    const [agencyTarget, setAgencyTarget] = useState('');
    const [agents, setAgents] = useState([]);
    const [agentTargets, setAgentTargets] = useState({}); // agent_id → { target, pct, actual }
    const [kpi, setKpi] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingAgency, setSavingAgency] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            // 1. Fetch rapide : team + targets (lancés en parallèle, mais sans attendre KPI)
            const [teamRes, agencyRes, agentRes] = await Promise.all([
                api.get('/users/team'),
                api.get('/targets/agency'),
                api.get('/targets/agents', { params: { month } }),
            ]);
            const agentsList = (teamRes.data || []).filter(u => ['agent', 'admin'].includes(u.role));
            setAgents(agentsList);

            const atMap = {};
            for (const t of (agentRes.data?.targets || [])) {
                atMap[t.agent_id] = { target: t.ca_target, id: t.agent_id };
            }
            setAgentTargets(atMap);

            // Agency target pour le mois sélectionné (affiché immédiatement)
            const agencyTargets = agencyRes.data?.targets || [];
            const current = agencyTargets.find(t => t.month === month) || agencyTargets.find(t => t.month === 'default');
            setAgencyTarget(current ? String(current.ca_target) : '');

            setLoading(false);

            // 2. Hydrater KPI en arrière-plan (peut prendre plusieurs secondes)
            try {
                const kpiRes = await api.get('/kpi/compute', { params: { ids: 'monthly_target_achievement,agent_ca_vs_target,pipeline_coverage' } });
                const agentKpi = kpiRes.data?.results?.agent_ca_vs_target?.meta?.agents || [];
                setAgentTargets(prev => {
                    const next = { ...prev };
                    for (const a of agentKpi) {
                        next[a.agent_id] = { ...(next[a.agent_id] || {}), actual: a.actual, pct: a.pct };
                    }
                    return next;
                });
                setKpi(kpiRes.data?.results);
            } catch (e) {
                // silent — KPIs restent à "—"
            }
        } catch (e) {
            toast.error('Erreur chargement objectifs');
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // `load` uses local `month` state — re-running when month changes is the desired behavior.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month]);

    const saveAgencyTarget = async () => {
        if (!agencyTarget || Number(agencyTarget) <= 0) {
            toast.error('Objectif invalide');
            return;
        }
        setSavingAgency(true);
        try {
            await api.post('/targets/agency', { ca_target: Number(agencyTarget), month });
            toast.success('Objectif agence enregistré');
            load();
        } catch (e) {
            toast.error('Erreur sauvegarde');
        } finally {
            setSavingAgency(false);
        }
    };

    const saveAgentTarget = async (agentId, value) => {
        if (!value || Number(value) <= 0) return;
        try {
            await api.post('/targets/agents', { agent_id: agentId, ca_target: Number(value), month });
            // Mise à jour optimiste immédiate pour le compteur "Agents configurés"
            setAgentTargets(prev => ({
                ...prev,
                [agentId]: { ...(prev[agentId] || {}), target: Number(value) }
            }));
            toast.success('Objectif agent enregistré');
        } catch (e) {
            toast.error('Erreur sauvegarde');
        }
    };

    const achievement = kpi?.monthly_target_achievement?.value;
    const coverage = kpi?.pipeline_coverage?.value;
    const agencyActual = kpi?.monthly_target_achievement?.meta?.actual || 0;

    return (
        <div className="min-h-screen bg-background p-4 sm:p-6 space-y-6" data-testid="targets-page">
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                        <Target className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <p className="text-[10px] text-[#FF914D] font-bold uppercase tracking-wider">Pilotage</p>
                        <h1 className="text-2xl md:text-3xl font-black text-foreground">Objectifs CA</h1>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground">
                    Définissez les objectifs mensuels pour débloquer les KPIs d'atteinte, de pipeline coverage et de classement agents.
                </p>
            </motion.div>

            {/* Month selector */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <input
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        className="bg-transparent text-sm text-foreground focus:outline-none"
                        data-testid="targets-month-picker"
                    />
                </div>
            </div>

            {/* Agency KPI tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-4 rounded-xl bg-gradient-to-br from-[#681793]/20 to-[#681793]/5 border border-[#681793]/20">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-3.5 h-3.5 text-[#9b4dca]" />
                        <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Atteinte objectif</p>
                    </div>
                    <p className="text-3xl font-black text-foreground">{achievement !== null && achievement !== undefined ? `${achievement}%` : '—'}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{formatEUR(agencyActual)} / {formatEUR(Number(agencyTarget) || 0)}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-[#FF914D]/20 to-[#FF914D]/5 border border-[#FF914D]/20">
                    <div className="flex items-center gap-2 mb-2">
                        <Target className="w-3.5 h-3.5 text-[#FF914D]" />
                        <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Pipeline coverage</p>
                    </div>
                    <p className="text-3xl font-black text-foreground">{coverage !== null && coverage !== undefined ? `${coverage}x` : '—'}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Sain si &gt; 3x</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                        <Users className="w-3.5 h-3.5 text-green-400" />
                        <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Agents configurés</p>
                    </div>
                    <p className="text-3xl font-black text-foreground">{Object.values(agentTargets).filter(t => t.target).length} / {agents.length}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Objectifs définis</p>
                </div>
            </div>

            {/* Agency target */}
            <div className="p-5 rounded-2xl bg-card border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                    <Euro className="w-4 h-4 text-[#FF914D]" />
                    <h2 className="text-base font-bold text-foreground">Objectif CA agence · {month}</h2>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                    <label className="flex-1 min-w-[200px]">
                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Objectif honoraires (€ HT)</span>
                        <input
                            type="number"
                            value={agencyTarget}
                            onChange={(e) => setAgencyTarget(e.target.value)}
                            placeholder="15000"
                            className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-sm focus:border-[#FF914D]/50 focus:outline-none"
                            data-testid="agency-target-input"
                        />
                    </label>
                    <Button
                        onClick={saveAgencyTarget}
                        disabled={savingAgency}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold"
                        data-testid="agency-target-save"
                    >
                        <Save className="w-3.5 h-3.5 mr-2" />
                        Enregistrer
                    </Button>
                </div>
            </div>

            {/* Agent targets */}
            <div className="p-5 rounded-2xl bg-card border border-white/10">
                <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-[#FF914D]" />
                    <h2 className="text-base font-bold text-foreground">Objectifs par agent · {month}</h2>
                </div>
                {loading ? (
                    <p className="text-sm text-muted-foreground">Chargement…</p>
                ) : agents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun agent dans l'agence.</p>
                ) : (
                    <div className="space-y-2">
                        {agents.map((agent) => {
                            const data = agentTargets[agent.id] || {};
                            return (
                                <AgentTargetRow
                                    key={agent.id}
                                    agent={agent}
                                    data={data}
                                    onSave={(value) => saveAgentTarget(agent.id, value)}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function AgentTargetRow({ agent, data, onSave }) {
    const [value, setValue] = useState(data.target ? String(data.target) : '');

    useEffect(() => {
        setValue(data.target ? String(data.target) : '');
    }, [data.target]);

    const pct = data.pct;
    const pctColor = pct >= 100 ? 'text-green-400' : pct >= 70 ? 'text-amber-400' : pct ? 'text-red-400' : 'text-muted-foreground';

    return (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors flex-wrap sm:flex-nowrap">
            <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground truncate">{agent.first_name} {agent.last_name}</p>
                <p className="text-[10px] text-muted-foreground">{agent.role === 'admin' ? 'Admin agence' : 'Agent'}</p>
            </div>
            <div className="text-right min-w-[80px]">
                <p className={`text-sm font-black ${pctColor}`}>{pct !== undefined ? `${pct}%` : '—'}</p>
                <p className="text-[10px] text-muted-foreground">{formatEUR(data.actual || 0)}</p>
            </div>
            <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Objectif €"
                className="w-28 px-2 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-foreground text-xs focus:border-[#FF914D]/50 focus:outline-none"
                data-testid={`agent-target-input-${agent.id}`}
            />
            <Button
                size="sm"
                onClick={() => onSave(value)}
                className="bg-white/10 hover:bg-white/15 text-foreground text-xs"
                data-testid={`agent-target-save-${agent.id}`}
            >
                OK
            </Button>
        </div>
    );
}
